<script lang="ts">
	import { Button, Text, Input } from '@Shelly/shelly-design-system';
	import {
		control_bluetooth_device_ios,
		control_bluetooth_dw_min_angel_threshold_ios,
	} from 'src/services/native_container_requests_ios';
	import {
		control_bluetooth_device_android,
		control_bluetooth_dw_min_angel_threshold_android,
	} from 'src/services/native_container_requests_android';

	import { app, os } from 'src/stores/session/native-bridge';
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	import { format } from 'svelte-i18n';
	import { valueOutOfRange } from 'src/helpers/utilities';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { bindSettingsDrilldownBack, bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import {
		isBleSettingsSessionReady,
		persistBleDeviceSetting,
		readStoredBleDevice,
		resumeBleSettingsConnectionIfNeeded,
	} from 'src/helpers/ble_settings_session';
	import DynamicAlert from './DynamicAlert.svelte';

	export let device;

	const min = 0;
	const max = 90;
	let minAngleThreshold: any = readStoredBleDevice(device)?.minAngleThreshold || min;
	let hadSession = false;

	$: sessionReady = isBleSettingsSessionReady($controlBleDevice.connectionState);
	$: if (sessionReady) hadSession = true;
	$: resumeBleSettingsConnectionIfNeeded(device.id, $controlBleDevice);
	$: controlsDisabled = !sessionReady || !$app;

	const goBackToSettingsList = bindSettingsDrilldownBack();

	const onSave = () => {
		if ($controlBleDevice.connectionState === 2) {
			$os === 'IOS'
				? control_bluetooth_dw_min_angel_threshold_ios(device.id, minAngleThreshold)
				: control_bluetooth_dw_min_angel_threshold_android(device.id, minAngleThreshold);

			persistBleDeviceSetting(device, { minAngleThreshold });
			goBackToSettingsList?.();
		} else {
			$os === 'IOS'
				? control_bluetooth_device_ios(device.id, 'connect')
				: control_bluetooth_device_android(device.id, 'connect');
		}
	};

	$: error = valueOutOfRange(minAngleThreshold, min, max)
		? $format('error_out_of_range', {
				default: `Value ${minAngleThreshold} is out of range. Please provide a value within the limits. (${min} — ${max})`,
				values: { value: minAngleThreshold, max, min, unit: '°' },
			})
		: '';

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: onSave,
				loading: false,
				disabled: controlsDisabled || !!error,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}
</script>

<DynamicAlert showExpiredMsg={hadSession} />

<Text class="mb-s ml-s" element="p" styleType="body-small">
	{$format('text_blu_setting_angle_threshold', {
		default: 'This setting determines the minimum rotation angle that will be sent as angle report.',
	})}
</Text>

<Input
	aria-label={$format('input_blu_setting_angle_threshold', {
		default:
			'Enter the minimum rotation angle that will be sent as angle report. Value must be between {min} — {max}!',
	})}
	icon="fas fa-angle"
	class="mb-m"
	bind:value={minAngleThreshold}
	type="number"
	step={1}
	{min}
	{max}
	disabled={controlsDisabled}
	info={$format('movement_limit_toast', {
		default: 'Values must be between {min} — {max}!',
		values: { min, max },
	})}
	{error}
	id="input-uvj5267"
/>

{#if !$showNewLayout}
	<Button disabled={controlsDisabled || !!error} size="m" width="full" on:click={onSave}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}
